import {
  LogoutOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { history, useIntl, useModel } from '@umijs/max';
import type { MenuProps } from 'antd';
import { Avatar, Spin } from 'antd';
import { createStyles } from 'antd-style';
import React from 'react';
import { flushSync } from 'react-dom';
import { outLogin } from '@/services/user/api';
import HeaderDropdown from '../HeaderDropdown';
import { removeToken } from '@/utils/utils';

export type GlobalHeaderRightProps = {
  menu?: boolean;
  children?: React.ReactNode;
};
const useStyles = createStyles(({ token }) => {
  return {
    avatar: {
      backgroundColor: token.colorPrimary,
      color: 'rgba(255,255,255)',
    },
    action: {
      display: 'flex',
      height: '48px',
      marginLeft: 'auto',
      overflow: 'hidden',
      alignItems: 'center',
      padding: '0 8px',
      cursor: 'pointer',
      borderRadius: token.borderRadius,
      '&:hover': {
        backgroundColor: token.colorBgTextHover,
      },
    },
  };
});

export const AvatarName = () => {
  const { styles } = useStyles();
  const { initialState } = useModel("@@initialState");
  const { currentUser } = initialState || {};
  const title = currentUser?.user.name || currentUser?.user?.email || " ";
  //-- 获取名称首字母
  const firstLetter = title.charAt(0).toUpperCase();
  return (
      <Avatar className={styles.avatar}>{firstLetter}</Avatar>
  );
};

export const AvatarDropdown: React.FC<GlobalHeaderRightProps> = ({
  menu,
  children,
}) => {
  const intl = useIntl();
  /**
   * 退出登录，并且将当前的 url 保存
   */
  const loginOut = async () => {
    await outLogin();
    removeToken();
    const { search, pathname } = window.location;
    const urlParams = new URL(window.location.href).searchParams;
    const searchParams = new URLSearchParams({
      redirect: pathname + search,
    });
    /** 此方法会跳转到 redirect 参数所在的位置 */
    const redirect = urlParams.get('redirect');
    // Note: There may be security issues, please note
    if (window.location.pathname !== '/user/login' && !redirect) {
      history.replace({
        pathname: '/user/login',
        search: searchParams.toString(),
      });
    }
  };
  const { styles } = useStyles();

  const { initialState, setInitialState } = useModel('@@initialState');

  const onMenuClick: MenuProps['onClick'] = (event) => {
    const { key } = event;
    if (key === 'logout') {
      flushSync(() => {
        setInitialState((s) => ({ ...s, currentUser: undefined }));
      });
      loginOut();
      return;
    }
    history.push(`/setting/${key}`);
  };

  const loading = (
    <span className={styles.action}>
      <Spin
        size="small"
        style={{
          marginLeft: 8,
          marginRight: 8,
        }}
      />
    </span>
  );

  if (!initialState) {
    return loading;
  }

  const { currentUser } = initialState;
  if (!currentUser || !currentUser.user?.name) {
    return loading;
  }

  const menuItems = [
    ...(menu
      ? [
          {
            key: "account",
            icon: <UserOutlined />,
            label: intl.formatMessage({
              id: "pages.account.setting",
              defaultMessage: "Account Settings",
            }),
          },
          {
            type: "divider" as const,
          },
        ]
      : []),
    {
      key: "logout",
      icon: <LogoutOutlined />,
      label: intl.formatMessage({
        id: "pages.login.logout",
        defaultMessage: "Logout",
      }),
    },
  ];

  return (
    <HeaderDropdown
      menu={{
        selectedKeys: [],
        onClick: onMenuClick,
        items: menuItems,
      }}
    >
      {children}
    </HeaderDropdown>
  );
};
